// src/api/axiosDefaults.js
// Global axios defaults for hybrid auth:
// - Always send cookies (Sanctum SPA)
// - Automatically attach Bearer token if present (mobile / legacy)

import axios from 'axios';
import Cookies from 'js-cookie';

axios.defaults.withCredentials = true;
axios.defaults.timeout = 15000;
axios.defaults.xsrfCookieName = 'XSRF-TOKEN';
axios.defaults.xsrfHeaderName = 'X-XSRF-TOKEN';

// axios only auto-attaches XSRF headers for same-origin by default.
// For cross-subdomain (dev-frontend -> dev-api) Sanctum SPA, enable it when supported.
if ('withXSRFToken' in axios.defaults) {
  axios.defaults.withXSRFToken = true;
}

export const getApiOrigin = () => {
  const base = process.env.REACT_APP_API_URL || '';
  // e.g. https://dev-api.irietix.com/api -> https://dev-api.irietix.com
  return base.replace(/\/api\/?$/, '');
};

export const ensureCsrfCookie = async () => {
  const origin = getApiOrigin();
  if (!origin) return;
  await axios.get(`${origin}/sanctum/csrf-cookie`, { withCredentials: true });
};

let csrfCookiePromise = null;

const isUnsafeMethod = (method) => {
  const m = String(method || 'get').toLowerCase();
  return m === 'post' || m === 'put' || m === 'patch' || m === 'delete';
};

const isCsrfCookieRequest = (url) => String(url || '').includes('/sanctum/csrf-cookie');

const readCookie = (name) => {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')}=([^;]*)`));
  if (!match) return undefined;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

const readXsrfToken = () => {
  const name = axios.defaults.xsrfCookieName;
  return Cookies.get(name) || readCookie(name);
};

axios.interceptors.request.use(
  async (config) => {
    // Authentication is handled exclusively via Sanctum SPA cookies (httpOnly).
    // No Bearer token is used to avoid token exposure via localStorage.
    if (isUnsafeMethod(config.method) && !isCsrfCookieRequest(config.url)) {
      const existingXsrf = readXsrfToken();
      if (!existingXsrf) {
        if (!csrfCookiePromise) {
          csrfCookiePromise = ensureCsrfCookie().catch((err) => {
            csrfCookiePromise = null;
            throw err;
          });
        }
        await csrfCookiePromise;
      }

      // Explicitly attach XSRF header for cross-origin requests.
      // (axios may skip this automatically when origin differs.)
      const xsrf = readXsrfToken();
      if (xsrf) {
        const headerName = axios.defaults.xsrfHeaderName;
        const hasHeader =
          (config.headers && typeof config.headers.get === 'function' && config.headers.get(headerName)) ||
          (config.headers && config.headers[headerName]);

        if (!hasHeader) {
          if (config.headers && typeof config.headers.set === 'function') {
            config.headers.set(headerName, xsrf);
          } else {
            config.headers = {
              ...(config.headers || {}),
              [headerName]: xsrf,
            };
          }
        }
      }
    }

    // For cross-subdomain Sanctum SPA.
    config.withCredentials = true;
    return config;
  },
  (error) => Promise.reject(error)
);
